/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.
*/

import type { VNode } from "preact";
import { AccessToken, TalerMerchantApi } from "@gnu-taler/taler-util";
import { BootstrapInstanceScreen } from "../screens/BootstrapInstanceScreen.js";
import { authenticate } from "./SignInRoute.js";
import type { LoginTokenInfo } from "../stores/session.js";
import { normalizeApiFailure } from "../api/failure.js";

export interface BootstrapInstanceRouteProps {
  backendUrl: string;
  onChangeServer: () => void;
  createFirstInstance: (
    payload: TalerMerchantApi.InstanceConfigurationMessage,
  ) => Promise<TalerMerchantApi.LoginTokenSuccessResponse | undefined>;
  onCreated: (data: {
    account: string;
    token: AccessToken;
    backendUrl: string;
    tokenInfo?: LoginTokenInfo;
  }) => void;
}

export function BootstrapInstanceRoute({
  backendUrl,
  onChangeServer,
  createFirstInstance,
  onCreated,
}: BootstrapInstanceRouteProps): VNode {
  return (
    <BootstrapInstanceScreen
      backendUrl={backendUrl}
      onChangeServer={onChangeServer}
      onSubmit={async (payload, password) => {
        const created = await createFirstInstance(payload);
        if (created?.access_token) {
          onCreated({
            account: "admin",
            token: created.access_token as AccessToken,
            backendUrl,
            tokenInfo: {
              expiresS:
                typeof created.expiration?.t_s === "number"
                  ? created.expiration.t_s
                  : undefined,
              refreshable: created.refreshable === true,
              scope: created.scope,
            },
          });
          return;
        }

        const authenticated = await authenticate({
          account: "admin",
          secret: password,
          backendUrl,
        });
        if (authenticated.type !== "ok") {
          throw authenticated.failure ?? normalizeApiFailure(authenticated);
        }
        if (!authenticated.token) {
          throw normalizeApiFailure(authenticated);
        }
        onCreated({
          account: "admin",
          token: authenticated.token as AccessToken,
          backendUrl,
          tokenInfo: authenticated.tokenInfo,
        });
      }}
    />
  );
}
